<template>
    <div
        class="template-hero section-wrapper"
        :class="viewport.is"
    >
        <section>
            <Chip
                variant="smoke"
                :text="{ size: 'xs', heading: false }"
            >
                Templates
            </Chip>
            <div class="grid">
                <div class="grid-left">
                    <GradientText
                        tag="h1"
                        :size="viewport.is.mobile ? 'xl' : 'xxl'" heading
                    >
                        Course templates
                    </GradientText>
                    <Txt>
                        Our training experts have built out training templates that you can edit directly in our builder. 
                    </Txt>
                    <Txt size="xs">
                        No sign up or credit card required.
                    </Txt>
                </div>
                <div class="grid-right">
                    <Button
                        variant="ghost"
                        size="xxs" heading
                        href="#onboarding"
                    >
                        Onboarding templates
                    </Button>
                    <Button
                        variant="ghost"
                        size="xxs" heading
                        href="#policies-&-procedures
"
                    >
                        Policy templates
                    </Button>
                    <ul>
                        <li>
                            <Icon name="correct" right />
                            <Txt>
                                No sign up required
                            </Txt>
                        </li>
                    </ul>
                </div>
            </div>
        </section>
    </div>
    <div
        class="template-image section-wrapper"
        :class="viewport.is"
    >
        <NuxtImg
            src="/bg-template-images.png"
            alt=""
            :modifiers="{ auto: 'compress,format', q: '90', dpr: '2' }"
            width="1638"
            height="624"
            sizes="xl:150vw lg:150vw md:150vw sm:150vw xs:150vw"
            loading="lazy"
            role="presentation"
            class="background"
        />
        <NuxtImg
            src="/template-splash.png"
            alt="Example of a template course"
            :modifiers="{ auto: 'compress,format', q: '90', dpr: '2' }"
            width="700"
            height="430"
            sizes="xl:80vw lg:80vw md:80vw sm:80vw xs:80vw"
            loading="lazy"
        />
    </div>
    <div
        class="templates section-wrapper"
        :class="viewport.is"
    >
        <section
            v-for="categoryTemplates, category in templates"
            :id="category.replace(/\s+/g, '-').toLowerCase()"
            :key="category"
        >
            <GradientText :size="42" heading>
                {{ category }}
            </GradientText>
            <div class="template-grid">
                <a
                    v-for="template, templateIndex in categoryTemplates"
                    :key="templateIndex"
                    :href="`/templates/${template.slug}`"
                >
                    <NuxtImg
                        v-if="template.thumbnail"
                        :src="template.thumbnail"
                        :alt="template.title"
                        provider="builderio"
                        width="2000"
                        height="1000"
                        sizes="xl:30vw lg:30vw md:30vw sm:30vw xs:30vw"
                        loading="lazy"
                    />
                </a>
            </div>
        </section>
    </div>
</template>

<script>
definePageMeta({
    layout: 'cms',
});

import { useResponsive } from '@/store/responsive';

import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Chip from '@/components/utility/chip';
import Button from '@/components/utility/button';
import GradientText from '@/components/animation/gradient-text';

export default {
    name: 'CourseTemplates',
    components: { Txt, Icon, Chip, Button, GradientText },
    inject: ['templates'],
    data() {
        return {
            viewport: { is: {} }
        };
    },
    mounted() {
        this.viewport = useResponsive();
    }
};
</script>

<style lang="less" scoped>
.template-hero.section-wrapper {
    section {
        align-items: flex-start;
        padding-bottom: 48px;
        gap: 24px;
        border-bottom: 1px solid var(--smoke);
        .chip {
            --chip-colour: var(--primary);
            padding: 8px 16px;
            height: auto;
        }
    }
    .grid {
        display: grid;
        grid-template-columns: 1fr 340px;
        gap: 164px;
        .grid-left {
            display: flex;
            flex-direction: column;
            gap: 24px;
            .txt-component.xs {
                color: var(--asphalt);
            }
            h1 { margin: 0; }
        }

        .grid-right {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 24px;
            padding: 24px 0;
            max-width: 340px;
            .cobutton {
                padding: 10px 24px;
                height: 40px;
            }
            .cobutton.ghost {
                border-radius: 40px;
                border-width: 1.5px;
            }
            ul { color: var(--primary); }
            .txt-component.xs { color: var(--asphalt); }
        }
    }
    &.tablet {
        padding: 40px;
        .grid {
            grid-template-columns: 1fr;
            gap: 24px;
            .grid-right {
                padding: 0;
            }
        }
    }
}
.template-image.section-wrapper {
    position: relative;
    margin: 40px;
    margin-top: 0;
    background-color: var(--navy);
    border-radius: 12px;
    display: flex;
    justify-content: center;
    img {
        position: relative;
        border-radius: 24px;
        &.background {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            border-radius: 12px;
        }
    }
    svg {
        position: absolute;
        top: 0;
    }
}
.section-wrapper.templates {
    padding: 80px;
    margin: 0 32px;
    section {
        align-items: flex-start;
        padding: 32px 0;
        gap: 40px;
    }

    .txt-component.heading { display: inline; }
    ul { color: var(--primary); }
    .template-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 16px;
        width: 100%;
        > a {
            aspect-ratio: 310 / 192;
            background-color: var(--smoke);
            box-shadow: var(--shadow-md);
            border-radius: 16px;
            transition: var(--smooth);
            &:hover {
                box-shadow: var(--shadow-lg);
                transform: scale(1.05);
            }
        }
    }
    &.lg {
        .section-grid {
            gap: 32px;
        }
    }
    &.tablet {
        padding: 24px;
        padding-bottom: 0;
        margin: 0;
        border-radius: 0;
        .template-grid {
            grid-template-columns: 1fr;
            gap: 24px;
        }
    }
}
</style>

<style lang="less">
.template-hero.section-wrapper {
    .chip .txt-component { font-weight: 700; }
}
.template-article.section-wrapper {
    article .builder-blocks {
        display: flex;
        flex-direction: column;
        gap: 56px;
        ul:not(.accordion) {
            list-style: disc;
            padding-left: 24px;
            margin: 12px 0;
            li { display: list-item; }
        }
    }
}
</style>
